// src/services/homeSectionsServices.js
import axios from "axios";

const apiUrl = `${process.env.REACT_APP_API_URL}/api/home-sections`;
// Récupère toujours le jeton le plus récent
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

/**
 * GET
 * Récupère toutes les sections de la Home.
 * @param {boolean} activeOnly - si true, ne renvoie que les sections actives
 * @returns {Promise<Array>} tableau de sections ([] en cas d'erreur)
 */
export const getAllHomeSections = async (activeOnly = true) => {
  try {
    const response = await axios.get(`${apiUrl}/getAllSections`, {
      params: { activeOnly },
      headers: authHeader(),
    });
    if (response.status !== 200) throw new Error("Failed to fetch data");
    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement des sections:", error);
    return [];
  }
};

/**
 * PATCH
 * Met à jour partiellement une section.
 * @param {number|string} id
 * @param {Object} patch - { section_name?, slug?, description?, position?, mediaURL?, is_active? }
 * @returns {Promise<Object|null>}
 */
export const updateHomeSection = async (id, patch) => {
  try {
    const response = await axios.patch(`${apiUrl}/updateSections/${id}`, patch, {
      headers: authHeader(),
    });
    if (response.status !== 200) throw new Error("Failed to update section");
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la mise à jour de la section:", error);
    return null;
  }
};

/**
 * PATCH
 * Toggle is_active d’une section.
 * @param {number|string} id
 * @returns {Promise<Object|null>}
 */
export const toggleHomeSectionActive = async (id) => {
  try {
    const response = await axios.patch(`${apiUrl}/toggleActive/${id}`, null, {
      headers: authHeader(),
    });
    if (response.status !== 200) throw new Error("Failed to toggle section");
    return response.data;
  } catch (error) {
    console.error("Erreur lors du toggle de la section:", error);
    return null;
  }
};

/**
 * PATCH
 * Change la position d’une section (réindexation côté serveur).
 * @param {number|string} id
 * @param {number} position - entier >= 1
 * @returns {Promise<Array|null>} liste complète réordonnée ou null
 */
export const changeHomeSectionPosition = async (id, position) => {
  try {
    const response = await axios.patch(
      `${apiUrl}/changePosition/${id}`,
      { position },
      { headers: authHeader() }
    );
    if (response.status !== 200) throw new Error("Failed to change position");
    return response.data;
  } catch (error) {
    console.error("Erreur lors du changement de position:", error);
    return null;
  }
};

/**
 * PATCH (multipart)
 * Met à jour l'image/vidéo (mediaURL) d’une section.
 * @param {number|string} id
 * @param {File} file - champ name "image"
 * @returns {Promise<{id:number, mediaURL:string}|null>}
 */
export const updateSectionHomeMediaURL = async (id, file) => {
  const formData = new FormData();
  formData.append("image", file);

  try {
    const response = await axios.patch(
      `${apiUrl}/${id}/changeMediaURL`,
      formData,
      {
        headers: {
          ...authHeader(),
          "Content-Type": "multipart/form-data",
        },
      }
    );
    if (response.status !== 200) throw new Error("Failed to update mediaURL");
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la mise à jour du mediaURL:", error);
    return null;
  }
};
